import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { billsAPI } from '../utils/api';
import { useSettings } from '../hooks/useSettings';
import toast from 'react-hot-toast';
import { format } from 'date-fns';

export default function Bills() {
  const { settings } = useSettings();
  const [bills, setBills] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const limit = 15;
  const cur = settings.currency || 'BDT';

  const fetchBills = useCallback(() => {
    setLoading(true);
    billsAPI.getAll({ search, status, page, limit })
      .then(res => { setBills(res.data.bills); setTotal(res.data.total); })
      .catch(() => toast.error('Failed to load bills'))
      .finally(() => setLoading(false));
  }, [search, status, page]);

  useEffect(() => { fetchBills(); }, [fetchBills]);

  const handleDelete = async (id, billNo) => {
    if (!window.confirm(`Delete bill ${billNo}?`)) return;
    try {
      await billsAPI.delete(id);
      toast.success('Bill deleted');
      fetchBills();
    } catch { toast.error('Failed to delete'); }
  };

  const pages = Math.ceil(total / limit);

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title" style={{ margin: 0 }}>Bills</h1>
        <Link to="/bills/new" className="btn btn-primary">+ New Bill</Link>
      </div>

      <div className="search-bar">
        <input
          className="form-control search-input"
          placeholder="Search by bill no. or client..."
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }}
        />
        <select
          className="form-control"
          style={{ width: 'auto', minWidth: 130 }}
          value={status}
          onChange={e => { setStatus(e.target.value); setPage(1); }}
        >
          <option value="">All Status</option>
          <option value="unpaid">Unpaid</option>
          <option value="partial">Partial</option>
          <option value="paid">Paid</option>
        </select>
      </div>

      <div className="card">
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Loading...</div>
        ) : bills.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">🧾</div>
            <div className="empty-state-text">No bills found</div>
            <Link to="/bills/new" className="btn btn-primary" style={{ marginTop: 16 }}>Create First Bill</Link>
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="table-wrapper desktop-table">
              <table>
                <thead>
                  <tr>
                    <th>Bill No.</th>
                    <th>Client</th>
                    <th>Date</th>
                    <th>Due Date</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {bills.map(b => (
                    <tr key={b.id}>
                      <td><span style={{ fontWeight: 700, color: '#2563eb' }}>{b.bill_number}</span></td>
                      <td>
                        <div style={{ fontWeight: 500 }}>{b.client_name}</div>
                        {b.client_address && <div style={{ fontSize: 12, color: '#94a3b8' }}>{b.client_address}</div>}
                      </td>
                      <td style={{ fontSize: 13, color: '#64748b' }}>{b.bill_date ? format(new Date(b.bill_date), 'dd MMM yyyy') : '-'}</td>
                      <td style={{ fontSize: 13, color: '#64748b' }}>{b.due_date ? format(new Date(b.due_date), 'dd MMM yyyy') : '-'}</td>
                      <td style={{ fontWeight: 700 }}>{cur} {parseFloat(b.total).toLocaleString()}</td>
                      <td><span className={`badge badge-${b.status}`}>{b.status}</span></td>
                      
                      <td>
  {b.is_recurring === 1 && (
    <span style={{ fontSize: 11, background: '#dbeafe', color: '#2563eb', padding: '2px 8px', borderRadius: 12, fontWeight: 600 }}>🔄 Recurring</span>
  )}
  {b.parent_bill_id && (
    <span style={{ fontSize: 11, background: '#f0fdf4', color: '#10b981', padding: '2px 8px', borderRadius: 12, fontWeight: 600 }}>♻️ Auto</span>
  )}
</td>

                      <td>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <Link to={`/bills/${b.id}`} className="btn btn-outline btn-sm">View</Link>
                          <Link to={`/bills/${b.id}/edit`} className="btn btn-outline btn-sm">Edit</Link>
                          <button className="btn btn-danger btn-sm" onClick={() => handleDelete(b.id, b.bill_number)}>Del</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="mobile-card-list" style={{ padding: '12px' }}>
              {bills.map(b => (
                <div className="mobile-card" key={b.id}>
                  <div className="mobile-card-row">
                    <span style={{ fontWeight: 700, color: '#2563eb', fontSize: 14 }}>{b.bill_number}</span>
                    <span className={`badge badge-${b.status}`}>{b.status}</span>
                  </div>
                  <div className="mobile-card-row">
                    <span className="mobile-card-label">Client</span>
                    <span className="mobile-card-value">{b.client_name}</span>
                  </div>
                  {b.client_address && (
                    <div className="mobile-card-row">
                      <span className="mobile-card-label">Address</span>
                      <span className="mobile-card-value">{b.client_address}</span>
                    </div>
                  )}
                  <div className="mobile-card-row">
                    <span className="mobile-card-label">Date</span>
                    <span className="mobile-card-value">{b.bill_date ? format(new Date(b.bill_date), 'dd MMM yyyy') : '-'}</span>
                  </div>
                  {b.due_date && (
                    <div className="mobile-card-row">
                      <span className="mobile-card-label">Due</span>
                      <span className="mobile-card-value">{format(new Date(b.due_date), 'dd MMM yyyy')}</span>
                    </div>
                  )}
                  <div className="mobile-card-row">
                    <span className="mobile-card-label">Amount</span>
                    <span className="mobile-card-value" style={{ fontWeight: 700 }}>{cur} {parseFloat(b.total).toLocaleString()}</span>
                  </div>
                  <div className="mobile-card-actions">
                    <Link to={`/bills/${b.id}`} className="btn btn-outline btn-sm">View</Link>
                    <Link to={`/bills/${b.id}/edit`} className="btn btn-outline btn-sm">Edit</Link>
                    <button className="btn btn-danger btn-sm" onClick={() => handleDelete(b.id, b.bill_number)}>Del</button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {pages > 1 && (
          <div className="pagination">
            <span style={{ fontSize: 13, color: '#64748b' }}>{total} bills</span>
            {Array.from({ length: pages }, (_, i) => (
              <button
                key={i}
                className={`btn btn-sm ${page === i + 1 ? 'btn-primary' : 'btn-outline'}`}
                onClick={() => setPage(i + 1)}
              >{i + 1}</button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}